/**
 * AdminLogsPage - live tail of system_log (Phase 12.3).
 *
 * Filter bar inputs (level, service, action, trace_id, since, until) are
 * debounced 300 ms so a user can type without hammering the API. The
 * auto-refresh toggle (on by default) drives the useAdminLogs hook's
 * ``refetchInterval`` knob. "Load older" appends a second query that
 * uses ``before_id`` set to the oldest currently-visible row.
 */
import { useEffect, useMemo, useState } from "react";

import { Alert, Button, Card, Input, Select, Spinner } from "@/components/ui";
import { LogRow } from "@/components/admin/LogRow";
import { useAdminLogs } from "@/hooks/useAdminLogs";
import { usePageTitle } from "@/hooks/usePageTitle";
import api from "@/lib/api";
import type {
  LogLevel,
  SystemLogQuery,
  SystemLogResponse,
} from "@/types/api";

const LEVEL_OPTIONS = [
  { value: "", label: "All levels" },
  { value: "DEBUG", label: "DEBUG" },
  { value: "INFO", label: "INFO" },
  { value: "WARNING", label: "WARNING" },
  { value: "ERROR", label: "ERROR" },
  { value: "CRITICAL", label: "CRITICAL" },
];

interface FilterState {
  level: "" | LogLevel;
  service: string;
  action: string;
  trace_id: string;
  since: string;
  until: string;
}

const EMPTY_FILTERS: FilterState = {
  level: "",
  service: "",
  action: "",
  trace_id: "",
  since: "",
  until: "",
};

function toQuery(f: FilterState): SystemLogQuery {
  const q: SystemLogQuery = {};
  if (f.level) q.level = f.level;
  if (f.service.trim()) q.service = f.service.trim();
  if (f.action.trim()) q.action = f.action.trim();
  if (f.trace_id.trim()) q.trace_id = f.trace_id.trim();
  if (f.since) q.since = new Date(f.since).toISOString();
  if (f.until) q.until = new Date(f.until).toISOString();
  return q;
}

export function AdminLogsPage(): JSX.Element {
  usePageTitle("System logs");

  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [debounced, setDebounced] = useState<FilterState>(EMPTY_FILTERS);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [olderRows, setOlderRows] = useState<SystemLogResponse[]>([]);
  const [olderError, setOlderError] = useState<string | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);

  // Debounce filter changes 300 ms.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(filters), 300);
    return () => clearTimeout(id);
  }, [filters]);

  // Reset the "load older" tail when filters change.
  useEffect(() => {
    setOlderRows([]);
    setOlderError(null);
  }, [debounced]);

  const query = useMemo(() => toQuery(debounced), [debounced]);
  const { data, isLoading, error, refetch, isFetching } = useAdminLogs({
    query,
    refetchInterval: autoRefresh ? 5_000 : false,
  });

  const rows = useMemo(() => {
    const head = data ?? [];
    return [...head, ...olderRows];
  }, [data, olderRows]);

  const handleLoadOlder = async () => {
    if (rows.length === 0) return;
    const tail = rows[rows.length - 1];
    if (!tail) return;
    setLoadingOlder(true);
    setOlderError(null);
    try {
      const response = await api.get<SystemLogResponse[]>("/admin/logs", {
        params: { ...query, before_id: tail.id },
      });
      setOlderRows((prev) => [...prev, ...response.data]);
    } catch (err) {
      setOlderError(
        err instanceof Error ? err.message : "Failed to load older logs.",
      );
    } finally {
      setLoadingOlder(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-7xl">
      <header className="mb-4 flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">
            System Logs
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            Live tail of structured operational logs. PII is redacted at
            write time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              aria-label="Auto-refresh"
            />
            Auto-refresh
          </label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            isLoading={isFetching && !autoRefresh}
          >
            Refresh
          </Button>
        </div>
      </header>

      <Card padding="sm" className="mb-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <Select
            id="logs-filter-level"
            label="Level"
            options={LEVEL_OPTIONS}
            value={filters.level}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                level: e.target.value as "" | LogLevel,
              }))
            }
          />
          <Input
            id="logs-filter-service"
            label="Service"
            value={filters.service}
            placeholder="auth_service"
            onChange={(e) =>
              setFilters((f) => ({ ...f, service: e.target.value }))
            }
          />
          <Input
            id="logs-filter-action"
            label="Action"
            value={filters.action}
            placeholder="login"
            onChange={(e) =>
              setFilters((f) => ({ ...f, action: e.target.value }))
            }
          />
          <Input
            id="logs-filter-trace"
            label="Trace id"
            value={filters.trace_id}
            placeholder="uuid"
            onChange={(e) =>
              setFilters((f) => ({ ...f, trace_id: e.target.value }))
            }
          />
          <Input
            id="logs-filter-since"
            label="Since"
            type="datetime-local"
            value={filters.since}
            onChange={(e) =>
              setFilters((f) => ({ ...f, since: e.target.value }))
            }
          />
          <Input
            id="logs-filter-until"
            label="Until"
            type="datetime-local"
            value={filters.until}
            onChange={(e) =>
              setFilters((f) => ({ ...f, until: e.target.value }))
            }
          />
        </div>
        <div className="mt-3 flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setFilters(EMPTY_FILTERS)}
          >
            Clear filters
          </Button>
        </div>
      </Card>

      {error && (
        <Alert variant="error" title="Failed to load logs" className="mb-4">
          {error instanceof Error ? error.message : "Unknown error."}
        </Alert>
      )}

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Timestamp
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Level
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Service
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Action
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Message
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Trace
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 bg-white">
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center">
                    <Spinner size="md" />
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-8 text-center text-sm text-neutral-500"
                  >
                    No log rows match this filter.
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <LogRow key={row.id} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-4 flex flex-col items-center gap-2">
        {olderError && (
          <Alert variant="error" title="Failed to load older rows">
            {olderError}
          </Alert>
        )}
        {rows.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void handleLoadOlder()}
            isLoading={loadingOlder}
          >
            Load older
          </Button>
        )}
      </div>
    </div>
  );
}

export default AdminLogsPage;
